
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export const useOrderDataLoader = (
  orderId: string, 
  handleBackToOrders: () => void,
  organizationId?: string | null
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerCountry, setCustomerCountry] = useState('');
  const [customerRegion, setCustomerRegion] = useState('');
  const [customerMunicipality, setCustomerMunicipality] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [products, setProducts] = useState<any[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const { toast } = useToast();

  // Load order data
  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch order data
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .select('*, customer_metadata, shipping_metadata, order_metadata, currency')
          .eq('id', orderId)
          .single();
          
        if (orderError) throw orderError;
        
        // Verify the order belongs to the current organization if we have an organizationId
        if (organizationId && orderData.organization_id !== organizationId) {
          toast({
            title: "Erreur",
            description: "Vous n'êtes pas autorisé à modifier cette commande",
            variant: "destructive",
          });
          handleBackToOrders();
          return;
        }
        
        // Set basic order data
        setOrderNumber(orderData.order_number || orderData.external_id || '');
        setOrderDate(orderData.order_date ? new Date(orderData.order_date).toISOString().split('T')[0] : 
                    orderData.created_at ? new Date(orderData.created_at).toISOString().split('T')[0] : '');
        setNotes(orderData.notes || '');
        setCustomerId(orderData.customer_id || '');
        
        // Get shipping address from shipping_metadata if exists
        if (orderData.shipping_metadata && orderData.shipping_metadata.address) {
          setShippingAddress(orderData.shipping_metadata.address);
        }
        
        // Get location data from shipping_metadata if exists
        if (orderData.shipping_metadata) {
          if (orderData.shipping_metadata.country) {
            setCustomerCountry(orderData.shipping_metadata.country);
          }
          if (orderData.shipping_metadata.region) {
            setCustomerRegion(orderData.shipping_metadata.region);
          }
          if (orderData.shipping_metadata.municipality) {
            setCustomerMunicipality(orderData.shipping_metadata.municipality);
          }
        }
        
        // Fetch customer data
        if (orderData.customer_id) {
          const { data: customerData, error: customerError } = await supabase
            .from('customers')
            .select('*')
            .eq('id', orderData.customer_id)
            .single();
            
          if (!customerError && customerData) {
            setCustomerName(customerData.name || 'Client inconnu');
            setCustomerPhone(customerData.phone || '');
            setCustomerEmail(customerData.email || '');
            
            // Set country/region/municipality from customer if not found in shipping_metadata
            if (!customerCountry && customerData.country) {
              setCustomerCountry(customerData.country);
            }
            if (!customerRegion && customerData.region) {
              setCustomerRegion(customerData.region);
            }
            if (!customerMunicipality && customerData.municipality) {
              setCustomerMunicipality(customerData.municipality);
            }
            
            setSelectedCustomer(customerData);
            
            // Set shipping address from customer data if not already set from shipping_metadata
            if (!shippingAddress && customerData.address) {
              setShippingAddress(customerData.address);
            }
          }
        }
        
        // Fetch order items
        const { data: itemsData, error: itemsError } = await supabase
          .from('order_items')
          .select('*, currency')
          .eq('order_id', orderId);
          
        if (itemsError) throw itemsError;
        
        // Process order items
        const orderProducts = [];
        let total = 0;
        
        // Default order currency (if exists in the order table)
        const orderCurrency = orderData.currency || 'DZD';
        
        if (itemsData && itemsData.length > 0) {
          for (const item of itemsData) {
            // Ensure price is a number
            const itemPrice = typeof item.price === 'number' ? item.price : 0;
            
            // Use item currency, fall back to order currency, then to default
            const itemCurrency = item.currency || orderCurrency || 'DZD';
            
            // Try to fetch product details if product_id exists
            let productDetails = {
              id: item.id || item.product_id || 'custom', // Use item.id as the primary identifier
              name: item.product_name || 'Produit personnalisé',
              price: itemPrice,
              image_url: '',
              product_id: item.product_id, // Keep the original product_id as a separate property
              currency: itemCurrency // Use the determined currency
            };
            
            if (item.product_id) {
              const { data: productData } = await supabase
                .from('products')
                .select('name, price, discount_price, currency')
                .eq('id', item.product_id)
                .single();
                
              if (productData) {
                const productPrice = typeof productData.price === 'number' ? productData.price : 0;
                
                productDetails = {
                  ...productDetails,
                  name: productData.name,
                  price: productData.discount_price || productPrice,
                  currency: productData.currency || itemCurrency,
                };
              }
            }
            
            const quantity = typeof item.quantity === 'number' ? item.quantity : 1;
            const itemTotal = productDetails.price * quantity;
            total += itemTotal;
            
            orderProducts.push({
              ...productDetails,
              quantity: quantity,
              price: productDetails.price,
              total: itemTotal,
              currency: productDetails.currency,
            });
          }
        }
        
        setProducts(orderProducts);
        setTotalAmount(total);
      } catch (error) {
        console.error('Error loading order:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les données de la commande",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchOrderData();
  }, [orderId, toast, handleBackToOrders, organizationId]);

  return {
    isLoading,
    customerId,
    customerName,
    customerPhone,
    customerEmail,
    customerCountry,
    customerRegion,
    customerMunicipality,
    selectedCustomer,
    orderNumber,
    orderDate,
    shippingAddress,
    notes,
    products,
    totalAmount,
    setCustomerId,
    setCustomerName,
    setCustomerPhone,
    setCustomerEmail,
    setCustomerCountry,
    setCustomerRegion,
    setCustomerMunicipality,
    setSelectedCustomer,
    setOrderNumber,
    setOrderDate,
    setShippingAddress,
    setNotes,
    setProducts,
    setTotalAmount
  };
};
